import React, { useState } from 'react';
import { Sparkles, ChevronDown, Loader2, Check, AlertCircle, Send } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { formatPersonName } from '../../utils/nameUtils';

const ACTION_LABELS = {
  classify_emails:     { icon: '📧', label: 'Classificar emails',  color: 'bg-blue-50 border-blue-200' },
  create_relationship: { icon: '🔗', label: 'Criar relação',       color: 'bg-purple-50 border-purple-200' },
  create_rule:         { icon: '📏', label: 'Criar regra',         color: 'bg-emerald-50 border-emerald-200' },
  link_domain:         { icon: '🌐', label: 'Vincular domínio',    color: 'bg-cyan-50 border-cyan-200' },
  create_empresa:      { icon: '🏢', label: 'Criar empresa',       color: 'bg-amber-50 border-amber-200' },
  create_contato:      { icon: '👤', label: 'Criar contato',       color: 'bg-amber-50 border-amber-200' },
  update_field:        { icon: '✏️', label: 'Atualizar campo',     color: 'bg-slate-50 border-slate-200' },
};

const ActionCard = ({ action, selected, onToggle, index }) => {
  const meta = ACTION_LABELS[action.type] || { icon: '⚡', label: action.type, color: 'bg-slate-50 border-slate-200' };
  const detailsText = action.details
    ? JSON.stringify(action.details, null, 1).slice(0, 180)
    : '';
  return (
    <label className={`flex items-start gap-2 p-1.5 rounded border cursor-pointer ${meta.color} ${selected ? 'ring-2 ring-indigo-400' : ''}`}>
      <input type="checkbox" checked={selected} onChange={() => onToggle(index)} className="mt-0.5 h-3.5 w-3.5" />
      <div className="flex-grow min-w-0">
        <div className="text-[11px] font-medium text-slate-800 flex items-center gap-1">
          <span>{meta.icon}</span> {meta.label}
        </div>
        <div className="text-[10px] text-slate-600 mt-0.5">{action.reasoning || ''}</div>
        {detailsText && (
          <pre className="text-[10px] text-slate-500 mt-0.5 whitespace-pre-wrap break-all">{detailsText}</pre>
        )}
      </div>
    </label>
  );
};

/**
 * AIContextChat — Context Loop AI-native Fase 1, componente generico.
 *
 * Renderiza um chat compacto onde o humano ensina a IA sobre relacoes de
 * negocio. A IA retorna understanding + acoes + perguntas; o humano marca
 * quais acoes aplicar. Ao aplicar, as acoes sao executadas via Supabase e
 * a interacao inteira e gravada em contexto_negocio (memoria institucional
 * consumida pelos agentes em execucoes futuras).
 *
 * Props:
 *   scope: { screen, email_ids?, oferta_id?, area_id?, empresa_id? }
 *   onActionsApplied?: () => void  // callback para o pai refetchar
 *   defaultCollapsed?: boolean
 */
const AIContextChat = ({ scope, onActionsApplied, defaultCollapsed = false }) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingActions, setPendingActions] = useState([]);
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [lastContextRecord, setLastContextRecord] = useState(null);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('agent-context-interpreter', {
        body: { text, scope, user_email: user?.email },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'interpretador falhou');

      const aiMessage = {
        role: 'ai',
        content: data.understanding || '(sem entendimento)',
        actions: data.actions || [],
        questions: data.questions || [],
      };
      setMessages(prev => [...prev, aiMessage]);
      setPendingActions(data.actions || []);
      setSelectedActions(new Set((data.actions || []).map((_, i) => i)));
      setLastContextRecord(data.context_record || null);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', content: `Erro: ${err.message || err}`, isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleAction = (idx) => {
    setSelectedActions(prev => {
      const n = new Set(prev);
      if (n.has(idx)) n.delete(idx); else n.add(idx);
      return n;
    });
  };

  const handleApply = async () => {
    if (pendingActions.length === 0 || selectedActions.size === 0 || loading) return;
    setLoading(true);
    const executed = [];

    const findEmpresaIdByName = async (nome) => {
      if (!nome) return null;
      const up = String(nome).toUpperCase().replace(/&/g, ' E ').replace(/\s+/g, ' ').trim();
      const { data } = await supabase.from('empresas').select('id').ilike('empresa', up).limit(1);
      return data?.[0]?.id || null;
    };

    for (const idx of Array.from(selectedActions).sort((a, b) => a - b)) {
      const action = pendingActions[idx];
      if (!action) continue;
      try {
        switch (action.type) {
          case 'classify_emails': {
            const { email_ids, oferta_id, empresa_id, confidence } = action.details || {};
            let count = 0;
            for (const eid of (email_ids || [])) {
              const patch = {
                classificacao_status: 'ai_sugerido',
                classificado_por: 'agent_context_interpreter',
                classificado_em: new Date().toISOString(),
              };
              if (oferta_id !== undefined) patch.oferta_id = oferta_id;
              if (empresa_id !== undefined) patch.empresa_id = empresa_id;
              if (typeof confidence === 'number') patch.classificacao_confianca = confidence;
              const { error: upErr } = await supabase.from('emails_gmail').update(patch).eq('id', eid);
              if (upErr) { console.warn('classify_emails:', upErr); continue; }
              count++;
              try {
                await supabase.from('ai_evidence').insert({
                  entity_type: 'email', entity_id: eid,
                  agent: 'agent_context_interpreter',
                  field_name: 'oferta_id',
                  suggested_value: oferta_id || null,
                  confidence: confidence ?? 0.9,
                  reasoning: action.reasoning || '',
                  human_decision: 'approved',
                  decided_by: user?.email || 'ui_context_chat',
                  metadata: { source: 'ai_context_chat', action_details: action.details || {} },
                });
              } catch { /* nao-fatal */ }
            }
            executed.push({ ...action, result: 'ok', count });
            break;
          }
          case 'link_domain': {
            const { domain, empresa_id } = action.details || {};
            if (!domain || !empresa_id) { executed.push({ ...action, result: 'skipped', reason: 'missing' }); break; }
            await supabase.from('dominios_empresa').insert({
              dominio: domain.replace(/^@/, '').toLowerCase(),
              empresa_id,
            }).select().then(r => r).catch(() => null);
            executed.push({ ...action, result: 'ok' });
            break;
          }
          case 'create_empresa': {
            const { nome, papel } = action.details || {};
            if (!nome) { executed.push({ ...action, result: 'skipped', reason: 'sem nome' }); break; }
            const up = String(nome).toUpperCase().replace(/&/g, ' E ').replace(/\s+/g, ' ').trim();
            const { data: exist } = await supabase.from('empresas').select('id').ilike('empresa', up).limit(1);
            if (exist && exist[0]) {
              executed.push({ ...action, result: 'already_existed', id: exist[0].id });
              break;
            }
            const { data: created, error: cErr } = await supabase.from('empresas').insert({
              empresa: up,
              company_name: up,
              papel_negocio: papel || 'service_provider',
              status_operacional: 'active',
            }).select('id').single();
            if (cErr) { executed.push({ ...action, result: 'error', error: cErr.message }); break; }
            executed.push({ ...action, result: 'ok', id: created.id });
            break;
          }
          case 'create_contato': {
            const { first_name, last_name, email, empresa_id } = action.details || {};
            if (!email && !first_name) { executed.push({ ...action, result: 'skipped' }); break; }
            const { data: exist } = await supabase.from('contatos').select('id')
              .eq('email', email || '').maybeSingle();
            if (exist?.id) { executed.push({ ...action, result: 'already_existed', id: exist.id }); break; }
            // 06/mai/26: nome em Title Case (ADR-001 PF).
            const { data: created, error: cErr } = await supabase.from('contatos').insert({
              first_name: formatPersonName(first_name) || null,
              last_name: formatPersonName(last_name) || null,
              email: email || null,
              empresa_id: empresa_id || null,
              tipo_contato: 'service_provider',
            }).select('id').single();
            if (cErr) { executed.push({ ...action, result: 'error', error: cErr.message }); break; }
            executed.push({ ...action, result: 'ok', id: created.id });
            break;
          }
          case 'create_relationship': {
            // Registra como nota em deal_context se houver oferta_id no scope;
            // senao fica apenas na memoria institucional via contexto_negocio + regras_derivadas.
            const det = action.details || {};
            if (scope?.oferta_id) {
              try {
                await supabase.from('deal_context').insert({
                  oferta_id: scope.oferta_id,
                  tipo: 'nota',
                  autor_tipo: 'ia',
                  autor_id: 'agent_context_interpreter',
                  canal_origem: 'context_chat',
                  texto: `Relação aprendida: ${det.from_empresa || '?'} é ${det.papel || 'relacionado'} de ${det.to_empresa || '?'}. ${det.context || ''}`,
                  metadata: { source: 'ai_context_chat', relationship: det },
                });
              } catch (e) { console.warn('relationship nota:', e); }
            }
            executed.push({ ...action, result: 'ok' });
            break;
          }
          case 'create_rule': {
            // A regra fica registrada em contexto_negocio.regras_derivadas abaixo;
            // aqui so marcamos que foi aceita.
            executed.push({ ...action, result: 'ok' });
            break;
          }
          case 'update_field': {
            const { table, row_id, field, value } = action.details || {};
            if (!table || !row_id || !field) { executed.push({ ...action, result: 'skipped' }); break; }
            const { error: upErr } = await supabase.from(table).update({ [field]: value }).eq('id', row_id);
            if (upErr) { executed.push({ ...action, result: 'error', error: upErr.message }); break; }
            executed.push({ ...action, result: 'ok' });
            break;
          }
          default:
            executed.push({ ...action, result: 'skipped', reason: 'tipo nao suportado' });
        }
      } catch (err) {
        executed.push({ ...action, result: 'error', error: err.message });
      }
    }

    // Gravar contexto_negocio (memoria institucional)
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    const lastAiMsg = [...messages].reverse().find(m => m.role === 'ai' && !m.isError);
    try {
      await supabase.from('contexto_negocio').insert({
        input_humano: lastUserMsg?.content || '',
        parsed_understanding: lastAiMsg?.content || '',
        parsed_entities: lastContextRecord?.parsed_entities || {},
        regras_derivadas: lastContextRecord?.regras_derivadas || [],
        acoes_executadas: executed,
        empresa_id: scope?.empresa_id || null,
        area_id: scope?.area_id || null,
        oferta_id: scope?.oferta_id || null,
        email_ids: scope?.email_ids || [],
        status: 'applied',
        criado_por: user?.email || 'desconhecido',
        validado_por: user?.email || 'desconhecido',
        validado_at: new Date().toISOString(),
        updated_by: user?.email || 'ui_context_chat',
      });
    } catch (e) {
      console.warn('[AIContextChat] contexto_negocio insert:', e);
    }

    const okCount = executed.filter(e => e.result === 'ok').length;
    const errCount = executed.filter(e => e.result === 'error').length;
    const summary = `${okCount} ação(ões) aplicada(s)${errCount ? `, ${errCount} erro(s)` : ''}. Memória institucional atualizada.`;
    setMessages(prev => [...prev, { role: 'ai', content: summary }]);
    setPendingActions([]);
    setSelectedActions(new Set());
    setLoading(false);

    if (onActionsApplied) onActionsApplied();
  };

  return (
    <div className="border border-indigo-200 rounded-lg bg-indigo-50/30">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100/50 rounded-t-lg"
      >
        <span className="flex items-center gap-1.5">
          <Sparkles size={14} />
          Contexto IA — ensine a IA sobre o negócio
        </span>
        <ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
            {messages.length === 0 && (
              <div className="text-[11px] text-indigo-500 italic py-1">
                Explique uma relação de negócio. Ex: <em>"Emails do @marcosgaviao.com.br são da oferta CONX Ruth Cardoso; eles são consultores de arquitetura."</em>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`text-xs ${msg.role === 'user' ? 'text-right' : ''}`}>
                {msg.role === 'user' ? (
                  <div className="inline-block bg-indigo-600 text-white rounded-lg px-3 py-1.5 max-w-[85%] text-left">
                    {msg.content}
                  </div>
                ) : (
                  <div className={`rounded-lg p-2.5 ${msg.isError ? 'bg-red-50 border border-red-200' : 'bg-white border border-indigo-200'}`}>
                    <div className={msg.isError ? 'text-red-800 flex items-start gap-1' : 'text-slate-700'}>
                      {msg.isError && <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />}
                      <span>{msg.content}</span>
                    </div>

                    {msg.actions?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="text-[10px] text-indigo-600 font-semibold uppercase">
                          Ações propostas ({msg.actions.length}):
                        </div>
                        {msg.actions.map((action, j) => (
                          <ActionCard
                            key={j}
                            action={action}
                            index={j}
                            selected={selectedActions.has(j)}
                            onToggle={toggleAction}
                          />
                        ))}
                      </div>
                    )}

                    {msg.questions?.length > 0 && (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                        <div className="text-[10px] text-amber-700 font-semibold uppercase">Preciso esclarecer:</div>
                        {msg.questions.map((q, k) => (
                          <div key={k} className="text-[11px] text-amber-800 mt-0.5">• {q}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-1.5 text-xs text-indigo-500">
                <Loader2 size={12} className="animate-spin" /> Processando...
              </div>
            )}
          </div>

          {pendingActions.length > 0 && selectedActions.size > 0 && (
            <button
              onClick={handleApply}
              disabled={loading}
              className="w-full bg-emerald-600 text-white text-xs font-semibold py-2 rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <Check size={12} /> Aplicar {selectedActions.size} ação{selectedActions.size !== 1 ? 'ões' : ''} selecionada{selectedActions.size !== 1 ? 's' : ''}
            </button>
          )}

          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              placeholder="Explique o contexto para a IA..."
              disabled={loading}
              className="flex-grow text-xs border border-indigo-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-indigo-400 outline-none disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Send size={11} /> Enviar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIContextChat;
